'use client';

import { useCallback, useEffect, useState, useTransition } from 'react';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { useRouter, usePathname } from 'next/navigation';
import type { Callback, VoidCallback } from '@/type-utils';
import type { ISearchSuggestions } from '@/services/models/SearchSuggestions';
import { SearchBoxVM } from '../view-models/SearchBoxVM';
import { mediaMatches } from '../components/core-ui/Breakpoints';

/**
 * Extra helper functions and properties returned by the `useSearchBox` hook.
 *
 * We have these extra properties to encapsulate the router logic and provide a
 * more convenient API for the view using `useSearchBox`.
 */
export interface IUseSearchBoxExtraReturns {
  /**
   * A boolean indicating whether the router is currently navigating to a new page.
   * This is useful to show a loading state or disable the search box while navigating.
   */
  isNavigatingToResults: boolean;
  /**
   * A function to navigate to the search results page.
   *
   * That encapsulates the router logic for the view using `useTransition`.
   */
  navigate: Callback<[string?]>;
  /**
   * A function to search for a term.
   *
   * Encapsulates media specific logic to get the products and terms count.
   */
  search: Callback<[string]>;
  /**
   * A function to reset the search box to the default state.
   */
  reset: VoidCallback;
}

/**
 * The return type of the `useSearchBox` hook.
 */
export type UseSearchBoxReturns = [
  // The SearchBoxVM instance
  SearchBoxVM,
  /** @inheritdoc */
  IUseSearchBoxExtraReturns
];

/**
 * `useSearchBox` returns a SearchBoxVM instance with initialized data and helper functions and properties.
 *
 * A search box is usually heavily attached to routing and navigation, so this hook encapsulates the logic
 * for cleaner API and better separation of concerns from the `SearchBoxVM`.
 *
 * @param initialSuggestions - An initial {@link ISearchSuggestions} to be used by the SearchBoxVM. If it's nullish, the
 * SearchBoxVM will be initialized with an empty search suggestions model and will initialize it client-side.
 *
 * @returns A tuple with the SearchBoxVM instance and helper functions and properties.
 *
 * @example
 * const [vm, helpers] = useSearchBox(); // A base search box with default term `''`
 */
export const useSearchBox = (
  initialSuggestions?: Promise<ISearchSuggestions | undefined>
): UseSearchBoxReturns => {
  // #region Navigation State Related Properties/Hooks
  const router = useRouter();
  /**
   * The `useEffect` that controls the initialization of the SearchBoxVM depends on the `pathname`
   * to reset the `SearchBoxVM` when the user navigates to a different page.
   *
   * That exists because the SearchBoxVM is not reactive to the router changes and usual places where it
   * may be used may not be re-rendered upon `AppRouter` navigation, for example the `Header` component.
   *
   * So we need to reset the `SearchBoxVM` when the user navigates to a different page to comply with the current
   * behavior on production sites.
   *
   * For more info see {@link https://deckersbrand.atlassian.net/browse/HP-4660?focusedCommentId=410923}.
   */
  const pathname = usePathname();
  const [isNavigatingToResults, startRouteTransition] = useTransition();
  // #endregion

  // #region SearchBoxVM Configuration Properties
  const config = ConfigurationService.getConfig('search');
  const productsCountSetting = config.getSetting(
    'searchSuggestions.productSuggestionsLimitOnSearch'
  ).value;
  const termsCountSetting = config.getSetting(
    'searchSuggestions.termsSuggestionsLimitOnSearch'
  ).value;
  const debounceTime = config.getSetting(
    'searchSuggestions.debounceTimeOnInput'
  ).value;
  // #endregion

  const [searchBoxVM] = useState<SearchBoxVM>(
    () => new SearchBoxVM(debounceTime, initialSuggestions)
  );

  const getProductAndTermsCount = useCallback(() => {
    /**
     * We need to set the products and terms count based on the
     * media type (desktop or mobile). This is done by checking the media matches
     * function from the Breakpoint component.
     *
     * See {@link BreakpointGroup} for more information.
     *
     * **Note**: This is not reactive, so if the media type changes after the component
     * mounts, the products and terms count will not update. This needs to be handled by an inner
     * <Breakpoints /> component that will update the VM search based on the media type.
     */
    const mediaType = mediaMatches(window, 'phone') ? 'mobile' : 'desktop';

    // Gets the products and terms count based on the media type.
    const productsCount = productsCountSetting[mediaType].value;
    const termsCount = termsCountSetting[mediaType].value;

    return { productsCount, termsCount };
  }, [productsCountSetting, termsCountSetting]);

  /**
   * A helper function to navigate to the search results page.
   *
   * This encapsulates the router logic for the view using useSearchBox.
   *
   * @param term - The term to search for. If not provided, the current term on the view model state will be used.
   */
  const navigate = useCallback(
    (term?: string): void => {
      const url = searchBoxVM.encodeSearchResultsPageNavigationUrl(term);
      searchBoxVM.saveTermToHistory(term);
      startRouteTransition(() => {
        router.push(url);
      });
    },
    [router, searchBoxVM]
  );

  /**
   * Resets the search box to the default state initializing with an empty search
   * for the actual matching device type (desktop or mobile).
   */
  const reset = useCallback((): void => {
    // Resets the VM updating the results for an empty term search.
    // This will populate the VM with the base suggestions for a search box.
    searchBoxVM.searchFor('', getProductAndTermsCount());
  }, [searchBoxVM, getProductAndTermsCount]);

  /**
   * A helper function to search for a term and prefetch the results page
   * using the router.
   */
  const search = useCallback(
    (term: string): void => {
      searchBoxVM.searchFor(term, getProductAndTermsCount());
    },
    [getProductAndTermsCount, searchBoxVM]
  );

  useEffect(
    function initialize() {
      // Initialize the search box by resetting it to a default `''` term.
      //
      // Even if the SearchBoxVM is created with server data, now we may need to consider
      // client relative data, so we need to reset the search box to the default state.
      reset();

      // Dispose resources from the SearchBoxVM when the component unmounts.
      // This is important to avoid memory leaks and unnecessary API calls.
      return () => {
        searchBoxVM.dispose();
      };
    },
    [reset, searchBoxVM, pathname]
  );

  return [searchBoxVM, { isNavigatingToResults, reset, navigate, search }];
};
